import { Injectable } from '@angular/core';
import { catchAndGetFromFailureRxOp } from '@geekbears/gb-ngrx-stack-base';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { map, mergeMap, withLatestFrom } from 'rxjs';
import { UsersServices } from '../../domain/services/users.service';
import { UsersActions } from './users.actions';
import { authenticatedUserIdSelector } from '@geekbears/gb-ngrx-auth-basic';
import { Store } from '@ngrx/store';

@Injectable()
export class UsersEffects {
  constructor(
    private actions$: Actions,
    private usersService: UsersServices,
    private store$: Store
  ) {}

  allUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getAllUsers),
      mergeMap((action) => {
        return this.usersService.getAllUsers(action.companyId).pipe(
          map((users) => UsersActions.getAllUsersSuccess({ users })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.getAllUsersFailure({ failure })
          )
        );
      })
    );
  });
  sendLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.sendLink),
      mergeMap((action) => {
        return this.usersService.sendLink().pipe(
          map((response) => UsersActions.sendLinkSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.sendLinkFailure({ failure })
          )
        );
      })
    );
  });
  uploadLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.uploadFile),
      mergeMap((action) => {
        return this.usersService.uploadFile(action.file).pipe(
          map((response) => UsersActions.uploadFileSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.uploadFileFailure({ failure })
          )
        );
      })
    );
  });

  usersCount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getUsersCount),
      mergeMap((action) => {
        return this.usersService.usersCount(action.companyId).pipe(
          map((response) => UsersActions.getUsersCountSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.getUsersCountFailure({ failure })
          )
        );
      })
    );
  });
  userDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getUserDetails),
      mergeMap((action) => {
        return this.usersService.userDetails(action.userId).pipe(
          map((users) => UsersActions.getUserDetailsSuccess({ user: users })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.getUserDetailsFailure({ failure })
          )
        );
      })
    );
  });
  deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      mergeMap((action) => {
        return this.usersService.deleteUser(action.userId).pipe(
          map((response) => UsersActions.deleteUserSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.deleteUserFailure({ failure })
          )
        );
      })
    );
  });
  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.updateUser),
      mergeMap((action) => {
        return this.usersService
          .updateUser(action.userId, action.userData)
          .pipe(
            map((response) => UsersActions.updateUserSuccess({ response })),
            catchAndGetFromFailureRxOp((failure) =>
              UsersActions.updateUserFailure({ failure })
            )
          );
      })
    );
  });
  forgotPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.forgotPassword),
      mergeMap((action) => {
        return this.usersService.forgotPassword(action.emailOrUsername).pipe(
          map((response) => UsersActions.forgotPasswordSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.forgotPasswordFailure({ failure })
          )
        );
      })
    );
  });
  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.resetPassword),
      mergeMap((action) => {
        return this.usersService.resetPassword(action.resetPasswordData).pipe(
          map((response) => UsersActions.resetPasswordSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.resetPasswordFailure({ failure })
          )
        );
      })
    );
  });
  updateCompanyManager$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.updateCompanyManager),
      withLatestFrom(this.store$.select(authenticatedUserIdSelector)),
      mergeMap(([action, userId]) => {
        return this.usersService
          .updateCompanyManager(userId!, action.userData)
          .pipe(
            map((response) =>
              UsersActions.updateCompanyManagerSuccess({ response })
            ),
            catchAndGetFromFailureRxOp((failure) =>
              UsersActions.updateCompanyManagerFailure({ failure })
            )
          );
      })
    );
  });

  confirmAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.confirmAccount),
      mergeMap((action) => {
        return this.usersService
          .confirmAccount(action.confirmAccountToken)
          .pipe(
            map((response) => UsersActions.confirmAccountSuccess({ response })),
            catchAndGetFromFailureRxOp((failure) =>
              UsersActions.confirmAccountFailure({ failure })
            )
          );
      })
    );
  });
  createCompanyManager$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.createCompanyManager),
      mergeMap((action) => {
        return this.usersService.createCompanyManager(action.data).pipe(
          map((response) =>
            UsersActions.createCompanyManagerSuccess({
              response: response.data,
            })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.createCompanyManagerFailure({ failure })
          )
        );
      })
    );
  });
  resendEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.resendEmail),
      mergeMap((action) => {
        return this.usersService.resendEmail().pipe(
          map((response) => UsersActions.resendEmailSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            UsersActions.resendEmailFailure({ failure })
          )
        );
      })
    );
  });
  sendCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.sendCode),
      mergeMap((action) => {
        return this.usersService
          .sendCode({ email: action.email, password: action.password })
          .pipe(
            map((response) => UsersActions.sendCodeSuccess({ response })),
            catchAndGetFromFailureRxOp((failure) =>
              UsersActions.sendCodeFailure({ failure })
            )
          );
      })
    );
  });
  verifyAuthToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.verifyAuthToken),
      mergeMap((action) => {
        return this.usersService
          .verifyAuthToken({ confirmAccountToken: action.confirmAccountToken })
          .pipe(
            map((response) =>
              UsersActions.verifyAuthTokenSuccess({
                response: response.data,
              })
            ),
            catchAndGetFromFailureRxOp((failure) =>
              UsersActions.verifyAuthTokenFailure({ failure })
            )
          );
      })
    );
  });
}
export const UsersEffectsModule = EffectsModule.forFeature([UsersEffects]);
